import * as _ from 'lodash-es';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import Logger from '../utils/Logger';

// eslint-disable-next-line slab-graphql/no-unused-fields
const TOPIC_TREE_LIST_FRAGMENT = gql(`
  fragment toTopicTreeList_topic on Topic {
    id
    name
    inheritParent
    parent {
      id
      name
    }
  }
`);

type TopicFragment = DocumentType<typeof TOPIC_TREE_LIST_FRAGMENT>;

export type TreeListTopic<T> = T & {
  ancestors: T[];
  childTopics: TreeListTopic<T>[];
  inheritedAncestors: T[];
};

function mapToTreeListTopic<T extends TopicFragment>(
  topics: T,
): TreeListTopic<T> {
  return { ...topics, ancestors: [], childTopics: [], inheritedAncestors: [] };
}

function populateAncestors<T extends TopicFragment>(
  currentParent: T,
  originalTopicMap: { [id: string]: T },
  ancestors: T[],
  visitedIds: Set<string>,
  inheritedAncestors?: T[],
) {
  if (visitedIds.has(currentParent.id)) {
    Logger.warn('Cycle detected in topic tree', {
      currentParentId: currentParent.id,
    });
    return;
  }
  const originalParentObject = originalTopicMap[currentParent.id];
  if (!originalParentObject) return;
  visitedIds.add(currentParent.id);
  ancestors.push(originalParentObject);
  inheritedAncestors?.push(originalParentObject);

  const nextParentId = originalParentObject?.parent?.id;
  const nextParent = nextParentId && originalTopicMap[nextParentId];

  if (nextParent) {
    populateAncestors(
      nextParent,
      originalTopicMap,
      ancestors,
      visitedIds,
      currentParent.inheritParent ? inheritedAncestors : undefined,
    );
  }
}

function toTopicTreeList<T extends TopicFragment>(
  topics: T[],
): TreeListTopic<T>[] {
  const topicNodes = topics.map(mapToTreeListTopic);
  const treeNodeMap = _.keyBy(topicNodes, 'id');
  const originalTopicMap = _.keyBy(topics, 'id');

  topicNodes.forEach((topic) => {
    if (topic.parent?.id == null) return;
    const parent = treeNodeMap[topic.parent.id];
    if (parent) {
      parent.childTopics.push(topic);
      populateAncestors(
        parent,
        originalTopicMap,
        topic.ancestors,
        new Set(),
        topic.inheritParent ? topic.inheritedAncestors : undefined,
      );
    }
  });

  const topicTree = topicNodes.filter(
    (topic) => !topic.parent?.id || !treeNodeMap[topic.parent.id],
  );

  return topicTree;
}

export const flattenTopicTreeList = <T extends TopicFragment>(
  topics: TreeListTopic<T>[],
): TreeListTopic<T>[] => {
  const flattenedAllTopics: TreeListTopic<T>[] = [];

  const flattenTopic = (topic: TreeListTopic<T>) => {
    flattenedAllTopics.push(topic);
    topic.childTopics.forEach((child) => flattenTopic(child));
  };

  topics.forEach((topic) => flattenTopic(topic));

  return flattenedAllTopics;
};

export const filterTreeListTopics = <T extends TopicFragment>(
  treeTopics: TreeListTopic<T>[],
  filter: (
    topic: Omit<TreeListTopic<T>, 'childTopics'>,
    level: number,
  ) => boolean,
  level = 0,
): TreeListTopic<T>[] => {
  return treeTopics
    .filter((topic) => filter(topic, level))
    .map((topic) => {
      const { childTopics, ...rest } = topic;
      return {
        ...rest,
        childTopics: filterTreeListTopics(childTopics, filter, level + 1),
      } as TreeListTopic<T>;
    });
};

export default toTopicTreeList;
